import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import type { CoreSuggestion, MentionSuggestion } from 'types/Suggestions';
import PAGE_VERIFIED from 'images/posts/pageVerifiedBadge.png';
import DemoBadge from 'app/components/DemoBadge';
import { Avatar } from '@kontentino/ui';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { HANDLE_AS_LABEL_PAGE_TYPES } from 'app/modules/textEditor/constants/mentionNetworks';

type Props = {
  items: (CoreSuggestion & Partial<MentionSuggestion>)[];
  command(editorMention: {
    label: string;
    id: string;
    link: string | null | undefined;
    role: string | undefined;
  }): void;
  isLoading?: boolean;
  /**
   * Known only by the page-mention path (`MentionSuggestions`, which already
   * carries `config.pageType`). Optional because the user-mention path
   * renders this list directly with no page context at all -- in which case
   * the label is always `name`, same as before this field existed.
   */
  pageType?: number;
  /**
   * Fired with the full chosen item, which `command`'s payload cannot carry
   * in full. Optional because the user-mention path renders this list
   * directly and has nothing to report.
   *
   * Typed as an arrow-typed property (not method shorthand) deliberately:
   * method shorthand is bivariant even under `strict`, which let a handler
   * requiring `item.kind` type-check here despite `kind` being optional on
   * this type. The arrow form makes TS check parameters contravariantly, so
   * a handler that actually needs `kind` has to say so and narrow for it
   * (see `MentionSuggestions.reportSelection`) instead of silently assuming
   * it.
   */
  onSelect?: (item: CoreSuggestion & Partial<MentionSuggestion>) => void;
};

export const SuggestionsList = forwardRef<any, Props>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      const usesHandleAsLabel =
        props.pageType !== undefined &&
        HANDLE_AS_LABEL_PAGE_TYPES.includes(props.pageType);

      // A handle that is missing or empty falls back to `name` rather than
      // inserting `@` or `@undefined` -- a mention that names the wrong
      // thing beats one that is malformed.
      const label = usesHandleAsLabel && item.handle ? item.handle : item.name;

      props.command({
        id: item.id,
        label,
        role: item.role,
        link: item.link,
      });

      props.onSelect?.(item);
    }
  };

  const keyDownHandler: Record<string, AnyFunction> = {
    ArrowUp: () =>
      setSelectedIndex(
        (selectedIndex + props.items.length - 1) % props.items.length,
      ),
    ArrowDown: () => setSelectedIndex((selectedIndex + 1) % props.items.length),
    Enter: () => selectItem(selectedIndex),
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: any) => {
      if (props.items.length === 0) {
        return false;
      }

      if (Object.keys(keyDownHandler).includes(event.key)) {
        keyDownHandler[event.key]();
        return true;
      } else {
        return false;
      }
    },
  }));

  if (props.items.length === 0 && !props.isLoading) {
    return null;
  }

  return (
    <div className="tw-rounded-lg tw-bg-white tw-p-2 tw-text-grayscale-180 tw-shadow-xl">
      <div
        className="tw-flex tw-max-h-[30vh] tw-flex-col tw-gap-2 tw-overflow-auto"
        ref={ref}
        data-cy="mention-suggestions_list"
        data-name="mention-suggestions_list"
      >
        {props.isLoading ? (
          <div className="tw-min-h-8 tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-md tw-p-2">
            <Skeleton circle width={20} height={20} className="tw-mr-2" />
            <div className="tw-flex-1">
              <Skeleton width={200} height={16} className="tw-mb-1" />
              <Skeleton width={150} height={12} />
            </div>
          </div>
        ) : (
          props.items.map((item, index) => (
            <button
              className={clsx(
                'tw-min-h-8 tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-md  tw-p-2 tw-text-left hover:tw-bg-primary-20',
                {
                  'tw-bg-primary-20': index === selectedIndex,
                },
              )}
              key={index}
              onClick={() => selectItem(index)}
              data-cy="mention-suggestions_item"
              data-name="mention-suggestions_item"
            >
              {item.image !== null && (
                <Avatar
                  src={item.image}
                  name={item.name}
                  size={20}
                  className="tw-mr-2"
                />
              )}
              <div>
                <div className="tw-flex tw-max-w-[300px] tw-items-center tw-text-left tw-text-md tw-font-medium">
                  <span className="tw-min-w-0 tw-truncate">{item.name}</span>
                  {item.isVerified && (
                    <img
                      src={PAGE_VERIFIED}
                      alt="page verified"
                      className="tw-ml-1 tw-h-3 tw-w-3"
                    />
                  )}
                  {item.isDemo && (
                    <div className="tw-ml-1">
                      <DemoBadge />
                    </div>
                  )}
                </div>
                <div className="tw-text-xs">{item.description}</div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
});
